import React from 'react';
import { Box, Typography } from '@mui/material';

interface QuickActionCardProps {
  label: string;
  icon: React.ReactElement;
  onClick: () => void;
  disabled?: boolean;
}

export const QuickActionCard: React.FC<QuickActionCardProps> = React.memo(({ label, icon, onClick, disabled }) => {
  return (
    <Box
      onClick={disabled ? undefined : onClick}
      onKeyDown={e => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 12px',
        gap: '8px',
        backgroundColor: '#FFFFFF',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: '16px',
        cursor: disabled ? 'default' : 'pointer',
        textAlign: 'left',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.02)',
        outline: 'none',
        pointerEvents: disabled ? 'none' : 'auto',
        opacity: disabled ? 0.6 : 1,
        '&:hover': disabled
          ? {}
          : {
              borderColor: '#111111',
              transform: 'translateY(-2px)',
              boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.06)',
              '& .icon-box': {
                backgroundColor: '#111111',
                color: '#FFFFFF',
              },
            },
        '&:active': {
          transform: 'translateY(0)',
        },
        '&:focus-visible': disabled
          ? {}
          : {
              borderColor: '#111111',
              boxShadow: '0 0 0 3px rgba(17, 17, 17, 0.2)',
            },
      }}
    >
      <Box
        className="icon-box"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '36px',
          minWidth: '36px',
          borderRadius: '10px',
          backgroundColor: '#F5F5F7',
          color: '#1A1A1A',
          transition: 'all 0.25s ease',
          flexShrink: 0,
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 20 } })}
      </Box>

      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          fontSize: '12.5px',
          color: '#1A1A1A',
          lineHeight: 1.2,
          textAlign: 'left',
          flex: 1,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
});

QuickActionCard.displayName = 'QuickActionCard';
